// Data provider REST wpięty w /admin/api (spec.md rozdz. 8.1).
//
// Gotowy @refinedev/simple-rest nie pasuje: zakłada nagłówek x-total-count
// i parametry _start/_end, a my zwracamy kopertę {items, total} z limit/offset.
// Własny provider to trzydzieści linii i zero rozjazdu z kontraktem backendu.

import type {
  BaseRecord,
  CustomParams,
  DataProvider,
  GetListParams,
  GetOneParams,
  UpdateParams,
} from "@refinedev/core";

import { API_URL, request } from "@/lib/http";
import { buildListQuery } from "@/lib/query";

interface Listing<T> {
  items: T[];
  total: number;
}

function unsupported(action: string): never {
  // Panel S4 nie zakłada zamówień ani przesyłek "z ręki". Gdyby ktoś podpiął
  // taki widok, ma o tym usłyszeć od razu, a nie zobaczyć ciche 404.
  throw new Error(`Panel nie obsługuje akcji "${action}" - to celowa decyzja, nie brak.`);
}

export const dataProvider: DataProvider = {
  getApiUrl: () => API_URL,

  getList: async <TData extends BaseRecord = BaseRecord>({
    resource,
    pagination,
    sorters,
    filters,
  }: GetListParams) => {
    const listing = await request<Listing<TData>>(`/${resource}`, {
      query: buildListQuery({ pagination, sorters, filters }),
    });
    return { data: listing.items, total: listing.total };
  },

  getOne: async <TData extends BaseRecord = BaseRecord>({ resource, id }: GetOneParams) => {
    const data = await request<TData>(`/${resource}/${id}`);
    return { data };
  },

  update: async <TData extends BaseRecord = BaseRecord, TVariables = unknown>({
    resource,
    id,
    variables,
  }: UpdateParams<TVariables>) => {
    const data = await request<TData>(`/${resource}/${id}`, {
      method: "PATCH",
      body: variables,
    });
    return { data };
  },

  create: async () => unsupported("create"),
  deleteOne: async () => unsupported("delete"),

  custom: async <TData extends BaseRecord = BaseRecord, TQuery = unknown, TPayload = unknown>({
    url,
    method,
    payload,
    query,
  }: CustomParams<TQuery, TPayload>) => {
    const data = await request<TData>(url, {
      method: method.toUpperCase() as "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
      body: payload,
      query: query ? new URLSearchParams(query as Record<string, string>) : undefined,
    });
    return { data };
  },
};
